import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CustomTemplate, LabelConfig } from '../types';
import { generateAiStickerArt } from '../lib/ai';
import { prepareAiArt, composeAiSticker, type AiLayout, type AiBorder, type ComposedSticker } from '../lib/aiSticker';
import { generateLabelSvg } from '../lib/svgGenerator';
import { ICONS } from '../constants';
import { getAiIdeas } from '../lib/aiIdeas';
import { useTextMode } from '../lib/textSettings';

const noIcon = ICONS.find(i => i.id === 'no-icon') ?? ICONS[0];

// Celá samolepka s AI: zákazník popíše obrázek (nebo zadá sadu motivů, nebo nahraje fotku),
// vybere styl, umístění jména a bílý okraj. AI nakreslí obrázky, aplikace z nich složí hotové
// samolepky s obrysem pro řez. Vybrané návrhy jdou na arch v jedné nebo ve třech velikostech.

const STYLES: { id: string; label: string }[] = [
  { id: 'cartoon', label: 'Kreslený' },
  { id: 'kawaii', label: 'Roztomilý (kawaii)' },
  { id: 'watercolor', label: 'Akvarel' },
  { id: 'simple', label: 'Jednoduchý' },
  { id: 'pixel', label: 'Pixel art' },
];
const LAYOUTS: { id: AiLayout; label: string }[] = [
  { id: 'below', label: 'Jméno pod obrázkem' },
  { id: 'side', label: 'Jméno vedle obrázku' },
  { id: 'none', label: 'Bez jména' },
];
const BORDERS: { id: AiBorder; label: string }[] = [
  { id: 'thin', label: 'Úzký' },
  { id: 'mid', label: 'Střední' },
  { id: 'wide', label: 'Široký' },
];
// Nápady se řídí tématem webu (/pejsci, /kocicky …) – viz lib/aiIdeas.ts a Administrace → Témata.
const AI_IDEAS = getAiIdeas();
const IDEAS = AI_IDEAS.ideas;
const SET_IDEAS = AI_IDEAS.sets;
type Mode = 'one' | 'set' | 'photo';
const MODES: { id: Mode; label: string }[] = [
  { id: 'one', label: 'Jeden motiv' },
  { id: 'set', label: 'Sada motivů' },
  { id: 'photo', label: 'Z fotky' },
];
type SizeMode = 'one' | 'three';

export interface AiStickerPick {
  template: CustomTemplate;
  ratio: number; // výška / šířka
  sizes: { w: number; count: number }[];
}

// výchozí šířka podle tvaru (na výšku / široká / hodně široká)
const baseWidth = (r: number) => (r >= 0.75 ? 30 : r >= 0.4 ? 44 : 60);
const sizesFor = (r: number, mode: SizeMode) => {
  const w = baseWidth(r);
  return mode === 'three'
    ? [{ w: Math.round(w * 1.5), count: 2 }, { w, count: 6 }, { w: Math.round(w * 0.67), count: 12 }]
    : [{ w, count: 6 }];
};

// fotku zmenšíme v prohlížeči (max 1024 px), ať se rychle odešle
async function shrinkPhoto(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('Fotku se nepodařilo načíst.')); i.src = url;
    });
    const k = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.88);
  } finally { URL.revokeObjectURL(url); }
}

interface Props {
  name: string;
  className?: string;
  onDone: (picks: AiStickerPick[]) => void;
  onClose: () => void;
}

const AiStickerDesigner: React.FC<Props> = ({ name, className, onDone, onClose }) => {
  const tm = useTextMode();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('cartoon');
  const [layout, setLayout] = useState<AiLayout>('below');
  const [border, setBorder] = useState<AiBorder>('mid');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('one');
  const [setItems, setSetItems] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [sizeMode, setSizeMode] = useState<SizeMode>('one');
  const [arts, setArts] = useState<HTMLCanvasElement[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const usedPrompt = useRef('');
  const fileRef = useRef<HTMLInputElement>(null);

  const subjects = setItems.split(/[,;\n]/).map(x => x.trim()).filter(Boolean).slice(0, 4);
  const canGenerate = !busy && (mode === 'one' ? !!prompt.trim() : mode === 'set' ? subjects.length > 0 : !!photo);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const generate = async () => {
    const p = prompt.trim();
    if (mode === 'one' && !p) { setError('Napište, co má být na obrázku.'); return; }
    if (mode === 'set' && !subjects.length) { setError('Napište motivy sady, oddělené čárkou.'); return; }
    if (mode === 'photo' && !photo) { setError('Nahrajte fotku.'); return; }
    setBusy(true); setError(null);
    try {
      const res = mode === 'set'
        ? await generateAiStickerArt({ subjects, style })
        : mode === 'photo'
          ? await generateAiStickerArt({ photo: photo!, prompt: p || undefined, style })
          : await generateAiStickerArt({ prompt: p, style });
      const prepared: HTMLCanvasElement[] = [];
      const names: string[] = [];
      for (let i = 0; i < res.images.length; i++) {
        try { prepared.push(await prepareAiArt(res.images[i])); names.push(res.subjects?.[i] || ''); } catch { /* prázdný obrázek přeskočit */ }
      }
      if (!prepared.length) throw new Error('AI nevrátila použitelný obrázek, zkuste to znovu.');
      usedPrompt.current = mode === 'set' ? subjects.join(', ') : mode === 'photo' ? `z fotky${p ? ` – ${p}` : ''}` : p;
      setArts(prepared); setLabels(names);
      setSelected(new Set(mode === 'set' ? prepared.map((_, i) => i) : [0]));
    } catch (e: any) { setError(e?.message || 'Generování selhalo.'); }
    finally { setBusy(false); }
  };

  const onPhoto = async (f?: File | null) => {
    if (!f) return;
    setError(null);
    try { setPhoto(await shrinkPhoto(f)); } catch (e: any) { setError(e?.message || 'Fotku se nepodařilo načíst.'); }
  };

  const toggle = (i: number) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  // složené samolepky – přepočítají se při změně rozložení nebo okraje
  const composed: ComposedSticker[] = useMemo(() => arts.map(a => composeAiSticker(a, layout, border)), [arts, layout, border]);

  const previewSvg = (c: ComposedSticker, i: number) => {
    const w = 40, h = Math.round(w * c.ratio * 10) / 10;
    const tpl: CustomTemplate = { id: `ai-prev-${i}`, name: 'AI', data: c.data, textZone: c.textZone, hasIcon: false, hasText: layout !== 'none', isLocal: true };
    const cfg: LabelConfig = {
      name: name || (tm.phrase ? 'Váš nápis' : 'Vaše jméno'), className: className || '', icon: noIcon,
      variantSettings: { p: { iconScale: 1, fontScale: 1, themeId: '', isFullImage: true, templateId: tpl.id, customWidth: w, customHeight: h, isBold: true, textAlign: 'center' } as any },
    };
    // s čarou řezu (fialová), ať zákazník vidí, kudy se bude řezat
    return { w, h, svg: generateLabelSvg(cfg, 'p', 0, 0, w, h, true, [], [], `aip${i}`, false, [tpl], 0, true) };
  };

  const use = () => {
    const stamp = Date.now();
    const picks: AiStickerPick[] = [...selected].sort((x, y) => x - y).map((i, k) => {
      const c = composed[i];
      return {
        ratio: c.ratio,
        sizes: sizesFor(c.ratio, sizeMode),
        template: {
          id: `ai-tpl-${stamp}-${k}`,
          name: `AI: ${labels[i] || usedPrompt.current}`.slice(0, 60),
          data: c.data,
          textZone: c.textZone,
          hasIcon: false,
          hasText: layout !== 'none',
          isLocal: true,
        },
      };
    }).filter(p => !!p.template.data);
    if (picks.length) onDone(picks);
  };

  const nSel = selected.size;
  const goLabel = busy ? 'Kreslím návrhy… (asi 20 s)'
    : mode === 'set' ? (arts.length ? 'Nakreslit sadu znovu' : `Nakreslit sadu (${subjects.length || 0})`)
    : arts.length ? 'Nakreslit jiné návrhy' : 'Nakreslit 4 návrhy';

  return (
    <div className="rda-ai-overlay" role="dialog" aria-modal="true" aria-labelledby="rda-ai-title">
      <div className="rda-ai">
        <header className="rda-ai-head">
          <h2 id="rda-ai-title">Celá samolepka s AI</h2>
          <button type="button" className="rda-ai-x" onClick={onClose} disabled={busy} aria-label="Zavřít">×</button>
        </header>
        <div className="rda-ai-body">
          <div className="rda-ai-form">
            <div className="rda-ai-modes" role="tablist" aria-label="Způsob tvorby">
              {MODES.map(m => <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} className={mode === m.id ? 'on' : ''} disabled={busy} onClick={() => { setMode(m.id); setError(null); }}>{m.label}</button>)}
            </div>

            {mode === 'one' && (<>
              <label className="rda-ai-label" htmlFor="rda-ai-prompt">Co má být na obrázku?</label>
              <textarea id="rda-ai-prompt" className="rda-ai-input" rows={2} value={prompt} maxLength={200}
                placeholder={`např. ${IDEAS[0]}`}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (canGenerate) generate(); } }} />
              <div className="rda-ai-ideas">
                {IDEAS.map(i => <button key={i} type="button" onClick={() => setPrompt(i)}>{i}</button>)}
              </div>
            </>)}

            {mode === 'set' && (<>
              <label className="rda-ai-label" htmlFor="rda-ai-set">Co má být v sadě?</label>
              <p className="rda-ai-hint rda-ai-hint-top">Napište až 4 motivy oddělené čárkou, nebo klikněte na rychlou volbu pod polem ({SET_IDEAS.map(i => i.label).join(', ')}).</p>
              <textarea id="rda-ai-set" className="rda-ai-input" rows={2} value={setItems} maxLength={300}
                placeholder={`např. ${SET_IDEAS[0]?.items || ''}`}
                onChange={e => setSetItems(e.target.value)} />
              <div className="rda-ai-ideas" aria-label="Rychlá volba sady">
                <span className="rda-ai-ideas-lbl">Rychlá volba:</span>
                {SET_IDEAS.map(i => <button key={i.label} type="button" onClick={() => setSetItems(i.items)}>{i.label}</button>)}
              </div>
              <p className="rda-ai-hint">Každý motiv se nakreslí zvlášť, všechny ve stejném stylu. Potom vyberete, které chcete použít.</p>
            </>)}

            {mode === 'photo' && (<>
              <span className="rda-ai-label">Fotka (dítě, zvířátko, hračka)</span>
              <input ref={fileRef} type="file" accept="image/*" className="rda-ai-file" onChange={e => { onPhoto(e.target.files?.[0]); e.target.value = ''; }} />
              <button type="button" className={`rda-ai-photo ${photo ? 'has' : ''}`} onClick={() => fileRef.current?.click()} disabled={busy}>
                {photo ? <img src={photo} alt="Nahraná fotka" /> : <span>Nahrát fotku</span>}
              </button>
              {photo && <button type="button" className="rda-ai-link" onClick={() => setPhoto(null)} disabled={busy}>Odebrat fotku</button>}
              <label className="rda-ai-label" htmlFor="rda-ai-prompt">Co přidat nebo zachovat (nepovinné)</label>
              <textarea id="rda-ai-prompt" className="rda-ai-input" rows={2} value={prompt} maxLength={200}
                placeholder={`např. ${AI_IDEAS.photoHint}`}
                onChange={e => setPrompt(e.target.value)} />
              <p className="rda-ai-hint">Fotku pošleme jen AI na překreslení, neukládáme ji.</p>
            </>)}

            <span className="rda-ai-label">Styl</span>
            <div className="rda-ai-opts">{STYLES.map(s => <button key={s.id} type="button" className={style === s.id ? 'on' : ''} aria-pressed={style === s.id} onClick={() => setStyle(s.id)}>{s.label}</button>)}</div>

            <span className="rda-ai-label">{tm.phrase ? 'Nápis' : 'Jméno'}</span>
            <div className="rda-ai-opts">{LAYOUTS.map(s => <button key={s.id} type="button" className={layout === s.id ? 'on' : ''} aria-pressed={layout === s.id} onClick={() => setLayout(s.id)}>{tm.phrase ? s.label.replace('Jméno', 'Nápis').replace('Bez jména', 'Bez nápisu') : s.label}</button>)}</div>

            <span className="rda-ai-label">Bílý okraj kolem</span>
            <div className="rda-ai-opts">{BORDERS.map(s => <button key={s.id} type="button" className={border === s.id ? 'on' : ''} aria-pressed={border === s.id} onClick={() => setBorder(s.id)}>{s.label}</button>)}</div>

            <span className="rda-ai-label">Velikosti na archu</span>
            <div className="rda-ai-opts">
              <button type="button" className={sizeMode === 'one' ? 'on' : ''} aria-pressed={sizeMode === 'one'} onClick={() => setSizeMode('one')}>Jedna velikost</button>
              <button type="button" className={sizeMode === 'three' ? 'on' : ''} aria-pressed={sizeMode === 'three'} onClick={() => setSizeMode('three')}>Tři velikosti</button>
            </div>
            {sizeMode === 'three' && <p className="rda-ai-hint">Každý vybraný návrh: 2× velký, 6× střední, 12× malý.</p>}

            {error && <p className="rda-ai-err" role="alert">{error}</p>}
          </div>

          <div className="rda-ai-results">
            {!arts.length && !busy && <p className="rda-ai-empty">Tady se ukážou návrhy. Samolepka bude vyříznutá podle obrázku – fialová čára ukazuje, kudy povede řez.</p>}
            {busy && <div className="rda-ai-empty"><span className="rda-ai-spin" aria-hidden="true" />AI kreslí obrázky…</div>}
            {!busy && composed.length > 0 && (
              <>
                <div className="rda-ai-grid">
                  {composed.map((c, i) => {
                    const p = previewSvg(c, i);
                    return (
                      <button key={i} type="button" className={`rda-ai-tile ${selected.has(i) ? 'on' : ''}`} aria-pressed={selected.has(i)} onClick={() => toggle(i)}>
                        {selected.has(i) && <span className="rdw-check" aria-hidden="true">✓</span>}
                        <svg viewBox={`-2 -2 ${p.w + 4} ${p.h + 4}`} dangerouslySetInnerHTML={{ __html: p.svg }} />
                        {labels[i] && <span className="rda-ai-cap">{labels[i]}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="rda-ai-note">{tm.phrase ? 'Kliknutím vyberete jeden nebo více návrhů. Fialová čára = řez. Nápis, velikost a počet můžete později změnit.' : 'Kliknutím vyberete jeden nebo více návrhů. Fialová čára = řez. Jméno, velikost a počet můžete později změnit.'}</p>
              </>
            )}
          </div>
        </div>
        <footer className="rda-ai-foot">
          <button type="button" className="rdw-btn-ghost" onClick={onClose} disabled={busy}>Zrušit</button>
          <div className="rda-ai-foot-r">
            {arts.length > 0 || busy
              ? <>
                  <button type="button" className="rdw-btn-ghost" onClick={generate} disabled={!canGenerate}>{goLabel}</button>
                  <button type="button" className="rdw-btn-primary" onClick={use} disabled={busy || !nSel}>{nSel > 1 ? `Použít vybrané (${nSel})` : 'Použít tento návrh'}</button>
                </>
              : <button type="button" className="rdw-btn-primary rda-ai-go" onClick={generate} disabled={!canGenerate}>{goLabel}</button>}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AiStickerDesigner;
